import React, { useEffect, useRef, useState } from 'react'
import {
  LuArrowRight,
  LuBookOpen,
  LuBrain,
  LuBrainCircuit,
  LuCalendarClock,
  LuChevronDown,
  LuClock3,
  LuFeather,
  LuHeart,
  LuMap,
  LuMessageCircle,
  LuMicVocal,
  LuMusic2,
  LuRefreshCcw,
  LuSparkles,
  LuSprout,
  LuSunrise,
  LuTimer,
  LuX,
} from 'react-icons/lu'
import FinalCtaPanel from '../FinalCtaPanel'
import AnimatedBrandMark from '../AnimatedBrandMark'
import { APP_ENTRY_HREF } from '../../constants/navigation'
import { PracticeMotion, PrincipleMotion } from './TipsInsightVisuals'
import TipsMemoryVisual, { TipsMemorySeed } from './TipsMemoryVisual'
import practiceScenes from '../../data/practiceScenes'
import './resources.css'

const memoryPrinciples = [
  {
    id: 'attrition',
    icon: LuBrain,
    labelFr: 'Mémoire',
    labelEn: 'Memory',
    titleFr: "Comprendre l’attrition",
    titleEn: 'Understanding attrition',
    textFr: "Oublier est normal quand une langue n’est pas utilisée. La réactivation est possible et naturelle.",
    textEn: 'Forgetting is normal when a language goes unused. Reactivation is both possible and natural.',
    takeawayFr: 'On ne repart pas de zéro.',
    takeawayEn: 'You are not starting from scratch.',
    detail: {
      whyFr: 'Une langue peu utilisée devient moins accessible, pas absente. Sons, tournures et automatismes restent souvent sous la surface : un indice familier peut rouvrir le chemin.',
      whyEn: 'An unused language becomes harder to access, not necessarily gone. Sounds, phrasing and automatic responses often remain beneath the surface; a familiar cue can reopen the path.',
      tryFr: 'Pensez à une situation que vous connaissiez bien. Dites trois phrases sans consulter de réponse, même incomplètes, puis vérifiez seulement après.',
      tryEn: 'Think of a situation you once knew well. Say three sentences without looking anything up—even incomplete ones—then check only afterward.',
      appFr: 'OctoSynapse vous replace dans une scène familière et laisse d’abord revenir ce qui est déjà là. Un indice doux apparaît seulement si le rappel bloque, puis s’efface quand votre voix reprend le relais.',
      appEn: 'OctoSynapse places you in a familiar scene and first lets what is already there return. A gentle cue appears only when recall stalls, then fades as your voice takes over.',
      sceneFr: 'Le café d’autrefois',
      sceneEn: 'The café you remember',
      promptFr: '« Comme d’habitude ? »',
      promptEn: '“The usual?”',
      responseFr: '« Oui… un café allongé, s’il vous plaît. »',
      responseEn: '“Yes… an americano, please.”',
      signalFr: 'chemin retrouvé',
      signalEn: 'pathway found',
      ctaFr: 'Réveiller ma langue',
      ctaEn: 'Wake up my language',
    },
  },
  {
    id: 'recall',
    icon: LuBrainCircuit,
    labelFr: 'Récupération',
    labelEn: 'Retrieval',
    titleFr: 'Rappel actif',
    titleEn: 'Active recall',
    textFr: 'Préférez récupérer les mots et expressions de mémoire plutôt que de simplement relire.',
    textEn: 'Retrieve words and phrases from memory instead of simply rereading them.',
    takeawayFr: 'Cherchez d’abord, vérifiez ensuite.',
    takeawayEn: 'Recall first. Check second.',
    detail: {
      whyFr: 'L’effort de retrouver crée une trace plus solide que la relecture. Même une réponse imparfaite signale au cerveau que ce chemin doit rester disponible.',
      whyEn: 'The effort of retrieval builds a stronger trace than rereading. Even an imperfect answer tells your brain that this pathway matters.',
      tryFr: 'Imaginez que le serveur demande : « Vous avez choisi ? » Répondez à voix haute avant de regarder une formulation possible, puis reformulez une seule fois.',
      tryEn: 'Imagine the server asks, “Have you decided?” Answer aloud before looking at a possible phrasing, then rephrase just once.',
      appFr: 'Une scène vous donne une intention, accueille votre réponse libre et ne révèle un indice qu’au moment utile. Vous repartez avec une reformulation réutilisable — jamais une note.',
      appEn: 'A scene gives you an intention, accepts your own response and reveals a cue only when useful. You leave with a reusable rephrase—never a grade.',
      sceneFr: 'Une table en terrasse',
      sceneEn: 'A table on the terrace',
      promptFr: '« Vous avez choisi ? »',
      promptEn: '“Have you decided?”',
      responseFr: '« Oui. Je vais prendre le plat du jour. »',
      responseEn: '“Yes. I’ll have today’s special.”',
      signalFr: 'rappel en cours',
      signalEn: 'retrieval live',
      ctaFr: 'Répondre dans une scène',
      ctaEn: 'Answer in a scene',
    },
  },
  {
    id: 'spacing',
    icon: LuCalendarClock,
    labelFr: 'Rythme',
    labelEn: 'Rhythm',
    titleFr: 'Répétition espacée',
    titleEn: 'Spaced repetition',
    textFr: "Revenez sur les phrases utiles à intervalles réguliers plutôt que tout revoir d’un coup.",
    textEn: 'Return to useful phrases at regular intervals instead of reviewing everything at once.',
    takeawayFr: 'Peu, souvent, durablement.',
    takeawayEn: 'A little, often, for lasting recall.',
    detail: {
      whyFr: 'Chaque récupération espacée reconstruit l’accès au moment où il commence à faiblir. Varier le contexte évite de mémoriser une seule formule figée.',
      whyEn: 'Each spaced retrieval rebuilds access as it begins to fade. Changing context prevents one rigid phrase from becoming the only memory.',
      tryFr: 'Aujourd’hui : dites une phrase utile. J1 : retrouvez-la sans aide. J3 : adaptez-la à un nouveau lieu. J7 : répondez librement.',
      tryEn: 'Today: say one useful phrase. D1: retrieve it unaided. D3: adapt it to a new setting. D7: respond freely.',
      appFr: 'OctoSynapse fait revenir une même intention dans de nouvelles scènes. L’intervalle, le soutien et le défi évoluent pour garder la mémoire active sans rendre la répétition mécanique.',
      appEn: 'OctoSynapse brings the same intention back in new scenes. Timing, support and challenge evolve to keep memory active without making repetition feel mechanical.',
      sceneFr: 'Une phrase, quatre retours',
      sceneEn: 'One phrase, four returns',
      promptFr: '« Pourriez-vous répéter, s’il vous plaît ? »',
      promptEn: '“Could you repeat that, please?”',
      responseFr: 'J0 · J1 · J3 · J7',
      responseEn: 'D0 · D1 · D3 · D7',
      signalFr: 'rythme ajusté',
      signalEn: 'rhythm tuned',
      ctaFr: 'Créer mon rythme',
      ctaEn: 'Build my rhythm',
    },
  },
  {
    id: 'emotion',
    icon: LuHeart,
    labelFr: 'Ancrage',
    labelEn: 'Anchoring',
    titleFr: 'Émotion + contexte',
    titleEn: 'Emotion + context',
    textFr: "La mémoire se renforce quand la langue est liée à des situations, des histoires et des émotions.",
    textEn: 'Memory grows stronger when language is tied to situations, stories and emotions.',
    takeawayFr: 'Une scène marque plus qu’une liste.',
    takeawayEn: 'A scene stays with you longer than a list.',
    detail: {
      whyFr: 'Un mot lié à un lieu, une voix, une intention et une émotion possède plusieurs portes d’entrée. Une scène revient donc plus facilement qu’une liste.',
      whyEn: 'A word tied to a place, voice, intention and emotion has several ways back in. That is why a scene returns more easily than a list.',
      tryFr: 'Choisissez une phrase que vous aimeriez dire à quelqu’un. Imaginez le lieu, entendez la voix, puis dites-la avec une intention réelle.',
      tryEn: 'Choose a phrase you would like to say to someone. Picture the place, hear the voice, then say it with real intention.',
      appFr: 'Café, réunion, galerie ou souvenir de famille : OctoSynapse relie la langue à une atmosphère et à des choix qui comptent. La pratique devient un moment auquel on veut revenir.',
      appEn: 'Café, meeting, gallery or family memory: OctoSynapse ties language to atmosphere and meaningful choices. Practice becomes a moment you want to return to.',
      sceneFr: 'Le marché du dimanche',
      sceneEn: 'The Sunday market',
      promptFr: '« Ça vous rappelle quoi ? »',
      promptEn: '“What does this remind you of?”',
      responseFr: '« Les étés chez ma grand-mère. »',
      responseEn: '“Summers at my grandmother’s.”',
      signalFr: 'souvenir ancré',
      signalEn: 'memory anchored',
      ctaFr: 'Entrer dans une scène',
      ctaEn: 'Enter a scene',
    },
  },
]

const practices = [
  {
    id: 'speak',
    icon: LuMessageCircle,
    labelFr: 'Oser',
    labelEn: 'Dare',
    titleFr: 'Parlez avant d’être prêt·e',
    titleEn: 'Speak before you feel ready',
    textFr: 'Une phrase hésitante réveille plus de langue qu’une réponse parfaite gardée en tête.',
    textEn: 'One hesitant sentence wakes up more language than a perfect answer kept in your head.',
    actionFr: 'Votre premier réflexe : répondre.',
    actionEn: 'Your first reflex: respond.',
  },
  {
    id: 'scenes',
    icon: LuMap,
    labelFr: 'Contextualiser',
    labelEn: 'Contextualize',
    titleFr: 'Ancrez chaque mot dans une scène',
    titleEn: 'Anchor every word in a scene',
    textFr: 'Un café, une gare, une réunion : le contexte rend les mots plus faciles à retrouver.',
    textEn: 'A café, a station, a meeting: context makes words easier to retrieve.',
    actionFr: 'La mémoire sait où revenir.',
    actionEn: 'Memory knows where to return.',
  },
  {
    id: 'vocabulary',
    icon: LuBookOpen,
    labelFr: 'Choisir',
    labelEn: 'Choose',
    titleFr: 'Réveillez les mots qui vous servent',
    titleEn: 'Wake up the words you need',
    textFr: 'Réactivez d’abord ce que vous voulez réellement dire, pas une liste entière.',
    textEn: 'Reactivate what you genuinely want to say first—not an entire list.',
    actionFr: 'Les bons mots, au bon moment.',
    actionEn: 'The right words, at the right moment.',
  },
  {
    id: 'voice',
    icon: LuMicVocal,
    labelFr: 'Écouter',
    labelEn: 'Listen',
    titleFr: 'Faites passer la langue par la voix',
    titleEn: 'Let the language travel through your voice',
    textFr: 'Écoutez, répétez, puis formulez votre propre réponse à voix haute.',
    textEn: 'Listen, repeat, then shape your own response out loud.',
    actionFr: 'Votre voix devient le chemin.',
    actionEn: 'Your voice becomes the pathway.',
  },
  {
    id: 'pleasure',
    icon: LuMusic2,
    labelFr: 'Aimer',
    labelEn: 'Enjoy',
    titleFr: 'Suivez ce qui vous donne envie',
    titleEn: 'Follow what makes you want to return',
    textFr: 'Musique, histoires et culture transforment la répétition en rendez-vous.',
    textEn: 'Music, stories and culture turn repetition into something to look forward to.',
    actionFr: 'L’envie vous fait revenir.',
    actionEn: 'Enjoyment keeps you coming back.',
  },
  {
    id: 'routine',
    icon: LuTimer,
    labelFr: 'Revenir',
    labelEn: 'Return',
    titleFr: 'Créez un rituel de cinq minutes',
    titleEn: 'Create a five-minute ritual',
    textFr: 'Un petit retour fréquent entretient mieux l’élan qu’une longue séance rare.',
    textEn: 'A short, frequent return sustains momentum better than a rare long session.',
    actionFr: '5 min · souvent · sans pression.',
    actionEn: '5 min · often · pressure-free.',
  },
]

const scenePresentation = {
  professional: { icon: LuMicVocal, tone: 'gold' },
  heritage: { icon: LuBookOpen, tone: 'magenta' },
  city: { icon: LuMap, tone: 'blue' },
  narrative: { icon: LuFeather, tone: 'green' },
  'cultural-visits': { icon: LuSparkles, tone: 'pink' },
  everyday: { icon: LuMessageCircle, tone: 'yellow' },
}

const sceneIdeas = practiceScenes.map((scene) => ({
  ...scene,
  ...scenePresentation[scene.id],
}))

const faqs = [
  {
    questionFr: "Pourquoi oublie-t-on une langue ?",
    questionEn: 'Why do we forget a language?',
    answerFr: "Parce que le cerveau renforce ce qu’il utilise et rend moins accessibles les chemins laissés au repos. Ils peuvent néanmoins être réactivés.",
    answerEn: 'Because the brain strengthens what it uses and makes dormant pathways less accessible. Yet they can still be reactivated.',
  },
  {
    questionFr: 'Combien de temps faut-il pour la raviver ?',
    questionEn: 'How long does it take to bring it back?',
    answerFr: 'Cela dépend de votre niveau initial et du temps écoulé. De courtes pratiques régulières font généralement revenir les automatismes progressivement.',
    answerEn: 'It depends on your starting level and how much time has passed. Short, regular practice usually brings automatic responses back gradually.',
  },
  {
    questionFr: 'Faut-il parler dès le début ?',
    questionEn: 'Should I start speaking right away?',
    answerFr: 'Oui, même avec des hésitations. Produire la langue crée un rappel actif plus puissant que la lecture seule.',
    answerEn: 'Yes—even with hesitation. Producing language creates more powerful active recall than reading alone.',
  },
  {
    questionFr: 'Comment mieux retenir le vocabulaire ?',
    questionEn: 'How can I remember vocabulary better?',
    answerFr: 'Utilisez des mots utiles dans des phrases, des situations concrètes et des rappels espacés plutôt que des listes isolées.',
    answerEn: 'Use useful words in sentences, real situations and spaced reviews instead of isolated lists.',
  },
  {
    questionFr: 'La culture aide-t-elle à mémoriser ?',
    questionEn: 'Does culture help memory?',
    answerFr: 'Oui. Histoires, musique, lieux et traditions donnent aux mots un contexte émotionnel qui les rend plus mémorables.',
    answerEn: 'Yes. Stories, music, places and traditions give words emotional context, making them more memorable.',
  },
  {
    questionFr: 'Puis-je progresser avec de courtes séances ?',
    questionEn: 'Can I progress with short sessions?',
    answerFr: 'Absolument. Cinq minutes de pratique régulière valent mieux qu’une longue séance occasionnelle, surtout pour raviver une langue.',
    answerEn: 'Absolutely. Five minutes of regular practice are more effective than an occasional long session, especially when bringing a language back.',
  },
]

const SectionHeading = ({ id, titleFr, titleEn }) => (
  <div className="tips-section-heading">
    <span aria-hidden="true" />
    <h2 id={id}>
      <span lang="fr">{titleFr}</span>
      <small lang="en">{titleEn}</small>
    </h2>
    <span aria-hidden="true" />
  </div>
)

const BilingualLabel = ({ as: Tag = 'span', className = '', fr, en, ...props }) => (
  <Tag className={`tips-bilingual-label ${className}`.trim()} {...props}>
    <span lang="fr">{fr}</span>
    <small lang="en">{en}</small>
  </Tag>
)

const BilingualBody = ({ className = '', fr, en }) => (
  <div className={`tips-bilingual-body ${className}`.trim()}>
    <p lang="fr">{fr}</p>
    <p lang="en">{en}</p>
  </div>
)

const LearningMotion = ({ type, icon: Icon, labelFr, labelEn }) => (
  <div className={`tips-learning-motion tips-learning-motion--${type}`} aria-hidden="true">
    <span className="tips-learning-motion__grid" />
    <svg
      className="tips-learning-motion__routes"
      viewBox="0 0 160 150"
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M80 78 C60 66 41 48 22 30" pathLength="1" />
      <path d="M80 78 C80 56 80 37 80 18" pathLength="1" />
      <path d="M80 78 C100 66 119 48 138 30" pathLength="1" />
      <path d="M80 78 C57 82 39 87 22 91" pathLength="1" />
      <path d="M80 78 C103 82 121 87 138 91" pathLength="1" />
    </svg>
    {Array.from({ length: 5 }, (_, index) => (
      <i
        className={`tips-learning-motion__node tips-learning-motion__node--${index + 1}`}
        style={{ '--learning-node-index': index }}
        key={index}
      />
    ))}
    <span className="tips-learning-motion__core">
      <i />
      <Icon aria-hidden="true" />
    </span>
    <BilingualLabel className="tips-learning-motion__label" fr={labelFr} en={labelEn} />
  </div>
)

const PrincipleSpotlight = ({
  principle,
  principleIndex,
  dialogRef,
  closeButtonRef,
  onClose,
}) => {
  const Icon = principle?.icon
  const titleId = principle ? `tips-principle-spotlight-title-${principle.id}` : undefined

  return (
    <dialog
      id="tips-principle-spotlight"
      className={`tips-principle-spotlight${principle ? ` tips-principle-spotlight--${principle.id}` : ''}`}
      ref={dialogRef}
      aria-labelledby={titleId}
      aria-modal="true"
      onCancel={(event) => {
        event.preventDefault()
        onClose()
      }}
      onKeyDown={(event) => {
        if (event.key !== 'Escape') return
        event.preventDefault()
        onClose()
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      {principle && (
        <div className="tips-principle-spotlight__shell" key={principle.id}>
          <button
            type="button"
            className="tips-principle-spotlight__close"
            ref={closeButtonRef}
            onClick={onClose}
          >
            <BilingualLabel fr="Fermer" en="Close" />
            <LuX aria-hidden="true" />
          </button>

          <div className="tips-principle-spotlight__visual">
            <span className="tips-principle-spotlight__grid" aria-hidden="true" />
            <svg
              className="tips-principle-spotlight__routes"
              viewBox="0 0 420 560"
              preserveAspectRatio="none"
              aria-hidden="true"
              focusable="false"
            >
              <path d="M210 276 C155 212 105 160 34 104" pathLength="1" />
              <path d="M210 276 C274 202 323 146 391 82" pathLength="1" />
              <path d="M210 276 C148 344 92 395 28 460" pathLength="1" />
              <path d="M210 276 C268 343 329 406 394 488" pathLength="1" />
            </svg>
            <span className="tips-principle-spotlight__orbit tips-principle-spotlight__orbit--outer" aria-hidden="true" />
            <span className="tips-principle-spotlight__orbit tips-principle-spotlight__orbit--inner" aria-hidden="true" />
            {Array.from({ length: 7 }, (_, moteIndex) => (
              <i
                className="tips-principle-spotlight__mote"
                style={{ '--spotlight-mote-index': moteIndex }}
                aria-hidden="true"
                key={moteIndex}
              />
            ))}

            <div className="tips-principle-spotlight__status">
              <span aria-hidden="true"><i /></span>
              <BilingualLabel fr="Mémoire activée" en="Memory activated" />
            </div>

            <div className="tips-principle-spotlight__diagram">
              <PrincipleMotion type={principle.id} icon={Icon} />
            </div>

            <div className="tips-principle-spotlight__identity">
              <span>{String(principleIndex + 1).padStart(2, '0')}</span>
              <BilingualLabel fr={principle.labelFr} en={principle.labelEn} />
            </div>
          </div>

          <div className="tips-principle-spotlight__content">
            <header className="tips-principle-spotlight__header">
              <BilingualLabel
                className="tips-principle-spotlight__eyebrow"
                fr="Le signal en profondeur"
                en="Inside the signal"
              />
              <BilingualLabel
                as="h2"
                className="tips-principle-spotlight__title"
                id={titleId}
                fr={principle.titleFr}
                en={principle.titleEn}
              />
              <span className="tips-principle-spotlight__takeaway">
                <LuSparkles aria-hidden="true" />
                <BilingualLabel fr={principle.takeawayFr} en={principle.takeawayEn} />
              </span>
            </header>

            <div className="tips-principle-spotlight__insights">
              <section className="tips-principle-spotlight__insight">
                <span className="tips-principle-spotlight__insight-icon" aria-hidden="true">
                  <LuBrainCircuit />
                </span>
                <BilingualLabel as="h3" fr="Pourquoi ça marche" en="Why it works" />
                <BilingualBody fr={principle.detail.whyFr} en={principle.detail.whyEn} />
              </section>
              <section className="tips-principle-spotlight__insight tips-principle-spotlight__insight--try">
                <span className="tips-principle-spotlight__insight-icon" aria-hidden="true">
                  <LuTimer />
                </span>
                <BilingualLabel as="h3" fr="Essayez en 30 secondes" en="Try it in 30 seconds" />
                <BilingualBody fr={principle.detail.tryFr} en={principle.detail.tryEn} />
              </section>
            </div>

            <section className="tips-principle-spotlight__experience">
              <div className="tips-principle-spotlight__experience-topbar">
                <span>
                  <i aria-hidden="true" />
                  <BilingualLabel fr="Dans OctoSynapse" en="Inside OctoSynapse" />
                </span>
                <BilingualLabel fr="Aperçu immersif" en="Immersive preview" />
              </div>

              <div className="tips-principle-spotlight__scene">
                <div className="tips-principle-spotlight__scene-signal" aria-hidden="true">
                  <span /><span /><span /><i />
                </div>
                <div className="tips-principle-spotlight__scene-copy">
                  <BilingualLabel
                    className="tips-principle-spotlight__scene-title"
                    fr={principle.detail.sceneFr}
                    en={principle.detail.sceneEn}
                  />
                  <BilingualLabel
                    className="tips-principle-spotlight__prompt"
                    fr={principle.detail.promptFr}
                    en={principle.detail.promptEn}
                  />
                  <BilingualLabel
                    className="tips-principle-spotlight__response"
                    fr={principle.detail.responseFr}
                    en={principle.detail.responseEn}
                  />
                </div>
                <span className="tips-principle-spotlight__scene-state">
                  <i aria-hidden="true" />
                  <BilingualLabel fr={principle.detail.signalFr} en={principle.detail.signalEn} />
                </span>
              </div>

              <BilingualBody
                className="tips-principle-spotlight__app-copy"
                fr={principle.detail.appFr}
                en={principle.detail.appEn}
              />
            </section>

            <footer className="tips-principle-spotlight__footer">
              <BilingualLabel
                className="tips-principle-spotlight__footer-note"
                fr="Votre langue n’attend qu’un premier signal."
                en="Your language is waiting for one first signal."
              />
              <a className="tips-principle-spotlight__cta" href={APP_ENTRY_HREF}>
                <BilingualLabel fr={principle.detail.ctaFr} en={principle.detail.ctaEn} />
                <LuArrowRight aria-hidden="true" />
              </a>
            </footer>
          </div>
        </div>
      )}
    </dialog>
  )
}

const useReveal = (threshold = 0.1) => {
  const ref = useRef(null)
  const [isVisible, setIsVisible] = useState(false)
  const [isInView, setIsInView] = useState(false)

  useEffect(() => {
    const node = ref.current
    if (!node) return undefined

    if (typeof window.IntersectionObserver !== 'function') {
      setIsVisible(true)
      setIsInView(true)
      return undefined
    }

    const observer = new window.IntersectionObserver(([entry]) => {
      setIsInView(entry.isIntersecting)
      if (entry.isIntersecting) setIsVisible(true)
    }, { threshold })

    observer.observe(node)
    return () => observer.disconnect()
  }, [threshold])

  return [ref, isVisible, isInView]
}

const Resources = () => {
  const [principlesRef, principlesVisible, principlesInView] = useReveal(0.12)
  const [practicesRef, practicesVisible, practicesInView] = useReveal(0.1)
  const [scenesRef, scenesVisible, scenesInView] = useReveal(0.08)
  const [learningRef, learningVisible, learningInView] = useReveal(0.1)
  const [activePrincipleId, setActivePrincipleId] = useState(null)
  const principleTriggerRefs = useRef({})
  const principleSpotlightRef = useRef(null)
  const principleSpotlightCloseRef = useRef(null)
  const activePrincipleIndex = memoryPrinciples.findIndex(({ id }) => id === activePrincipleId)
  const activePrinciple = activePrincipleIndex >= 0
    ? memoryPrinciples[activePrincipleIndex]
    : null

  useEffect(() => {
    const dialog = principleSpotlightRef.current
    if (!dialog) return undefined

    if (!activePrinciple) {
      if (dialog.hasAttribute('open')) {
        if (typeof dialog.close === 'function') {
          try {
            dialog.close()
          } catch {
            dialog.removeAttribute('open')
          }
        } else {
          dialog.removeAttribute('open')
        }
      }
      return undefined
    }

    if (!dialog.hasAttribute('open')) {
      if (typeof dialog.showModal === 'function') {
        try {
          dialog.showModal()
        } catch {
          dialog.setAttribute('open', '')
        }
      } else {
        dialog.setAttribute('open', '')
      }
    }

    const focusCloseButton = () => principleSpotlightCloseRef.current?.focus({ preventScroll: true })
    if (typeof window.requestAnimationFrame !== 'function') {
      focusCloseButton()
      return undefined
    }

    const focusFrame = window.requestAnimationFrame(focusCloseButton)
    return () => window.cancelAnimationFrame?.(focusFrame)
  }, [activePrinciple])

  useEffect(() => {
    if (!activePrinciple) return undefined

    const previousOverflow = document.body.style.overflow
    const previousPaddingRight = document.body.style.paddingRight
    const documentWidth = document.documentElement.clientWidth
    const scrollbarWidth = documentWidth > 0 ? Math.max(0, window.innerWidth - documentWidth) : 0

    if (scrollbarWidth > 0) {
      const currentPaddingRight = Number.parseFloat(window.getComputedStyle(document.body).paddingRight) || 0
      document.body.style.paddingRight = `${currentPaddingRight + scrollbarWidth}px`
    }
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
      document.body.style.paddingRight = previousPaddingRight
    }
  }, [activePrinciple])

  const openPrincipleSpotlight = (principleId, trigger) => {
    const cardBounds = trigger.closest('.tips-principle-card')?.getBoundingClientRect()
    const dialog = principleSpotlightRef.current

    if (cardBounds && dialog) {
      const cardCenterX = cardBounds.left + (cardBounds.width / 2)
      const cardCenterY = cardBounds.top + (cardBounds.height / 2)
      dialog.style.setProperty('--spotlight-entry-x', `${Math.round(cardCenterX - (window.innerWidth / 2))}px`)
      dialog.style.setProperty('--spotlight-entry-y', `${Math.round(cardCenterY - (window.innerHeight / 2))}px`)
    }

    setActivePrincipleId(principleId)
  }

  const closePrincipleSpotlight = () => {
    if (!activePrincipleId) return

    const triggerToRestore = principleTriggerRefs.current[activePrincipleId]
    const dialog = principleSpotlightRef.current

    if (dialog?.hasAttribute('open')) {
      if (typeof dialog.close === 'function') {
        try {
          dialog.close()
        } catch {
          dialog.removeAttribute('open')
        }
      } else {
        dialog.removeAttribute('open')
      }
    }

    setActivePrincipleId(null)

    const restoreFocus = () => triggerToRestore?.focus({ preventScroll: true })
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(restoreFocus)
    } else {
      restoreFocus()
    }
  }

  return (
    <main className="tips-page" id="resources" lang="fr" tabIndex="-1">
    <section className="tips-hero" aria-labelledby="tips-title">
      <TipsMemoryVisual />

      <div className="tips-hero__copy">
        <p className="tips-hero__eyebrow">
          <i aria-hidden="true" />
          <span lang="fr">Méthode de réactivation</span>
          <small lang="en">Reactivation method</small>
        </p>
        <h1 id="tips-title">
          <span className="tips-page__title-fr" lang="fr">
            Des conseils pour
            <strong>raviver votre langue.</strong>
          </span>
          <small className="tips-page__title-en" lang="en">
            Practical guidance to <em>bring your language back.</em>
          </small>
        </h1>
        <BilingualLabel
          as="p"
          className="tips-hero__lead"
          fr={<>Comprenez l’attrition. Mémorisez <br />mieux. Pratiquez avec plaisir.</>}
          en={<>Understand attrition. Remember better.<br />Practice with joy.</>}
        />
        <BilingualBody
          className="tips-hero__body"
          fr="De petites habitudes, un lien émotionnel, le rappel actif et la pratique en contexte sont les clés pour garder une langue vivante — ou la remettre en mouvement."
          en="Small habits, emotional connection, active recall and practice in context are the keys to keeping a language alive—or setting it back in motion."
        />

        <div className="tips-hero__benefits">
          <div>
            <LuClock3 aria-hidden="true" />
            <BilingualLabel as="p" fr={<><strong>5 minutes</strong><span>par jour</span></>} en={<><strong>5 minutes</strong><span>a day</span></>} />
          </div>
          <div>
            <LuBrainCircuit aria-hidden="true" />
            <BilingualLabel as="p" fr={<><strong>Fondé sur des</strong><span>bonnes pratiques</span></>} en={<><strong>Grounded in</strong><span>proven practices</span></>} />
          </div>
          <div>
            <LuFeather aria-hidden="true" />
            <BilingualLabel as="p" fr={<><strong>Sans</strong><span>pression</span></>} en={<><strong>No</strong><span>pressure</span></>} />
          </div>
        </div>

      </div>
    </section>

    <section
      className={`tips-section tips-showcase tips-showcase--principles${principlesVisible ? ' tips-showcase--visible' : ''}${principlesInView ? ' tips-showcase--in-view' : ''}`}
      ref={principlesRef}
      aria-labelledby="memory-principles-title"
    >
      <SectionHeading
        id="memory-principles-title"
        titleFr={<>Ce qui aide <strong>vraiment</strong></>}
        titleEn={<>What <strong>truly helps</strong></>}
      />
      <BilingualLabel
        as="p"
        className="tips-showcase__intro"
        fr="Quatre mécanismes simples remettent le signal en mouvement. Sélectionnez une carte pour ouvrir l’expérience, comprendre pourquoi elle fonctionne et l’essayer."
        en="Four simple mechanisms set the signal back in motion. Select a card to open the experience, see why it works, and try it for yourself."
      />
      <div className="tips-principles-lab">
        <div className="tips-principles-hub" aria-hidden="true">
          <AnimatedBrandMark className="tips-principles-hub__mark" />
          <span>
            <BilingualLabel as="strong" fr="Mémoire en mouvement" en="Memory in motion" />
            <BilingualLabel fr="4 leviers reliés" en="4 connected levers" />
          </span>
        </div>
        <svg className="tips-principles-routes" viewBox="0 0 1000 420" preserveAspectRatio="none" aria-hidden="true" focusable="false">
          <path d="M500 44 C500 95 235 64 235 150" pathLength="1" />
          <path d="M500 44 C500 95 765 64 765 150" pathLength="1" />
          <path d="M500 44 C500 175 235 250 235 330" pathLength="1" />
          <path d="M500 44 C500 175 765 250 765 330" pathLength="1" />
        </svg>
        <div className={`tips-principles${activePrinciple ? ' tips-principles--engaged' : ''}`}>
          {memoryPrinciples.map(({
            id,
            icon: Icon,
            labelFr,
            labelEn,
            titleFr,
            titleEn,
            textFr,
            textEn,
            takeawayFr,
            takeawayEn,
          }, index) => (
            <article
              className={`tips-principle-card tips-principle-card--${id}${activePrincipleId === id ? ' tips-principle-card--active' : ''}`}
              data-tips-principle={id}
              style={{ '--card-index': index }}
              key={id}
            >
              <button
                type="button"
                className="tips-principle-card__trigger"
                ref={(node) => {
                  if (node) principleTriggerRefs.current[id] = node
                  else delete principleTriggerRefs.current[id]
                }}
                aria-expanded={activePrincipleId === id}
                aria-controls="tips-principle-spotlight"
                aria-haspopup="dialog"
                onClick={(event) => openPrincipleSpotlight(id, event.currentTarget)}
              >
                <BilingualLabel
                  className="tips-principle-card__trigger-label"
                  fr={`Découvrir : ${titleFr}`}
                  en={`Explore: ${titleEn}`}
                />
              </button>
              <PrincipleMotion type={id} icon={Icon} />
              <div className="tips-principle-card__copy">
                <div className="tips-principle-card__meta">
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <BilingualLabel className="tips-card-meta-label" fr={labelFr} en={labelEn} />
                </div>
                <BilingualLabel as="h3" fr={titleFr} en={titleEn} />
                <BilingualBody className="tips-principle-card__body" fr={textFr} en={textEn} />
                <div className="tips-principle-card__footer">
                  <span className="tips-principle-card__takeaway">
                    <LuSparkles aria-hidden="true" />
                    <BilingualLabel fr={takeawayFr} en={takeawayEn} />
                  </span>
                  <span className="tips-principle-card__reveal" aria-hidden="true">
                    <BilingualLabel
                      fr={activePrincipleId === id ? 'Ouvert' : 'Découvrir'}
                      en={activePrincipleId === id ? 'Open' : 'Explore'}
                    />
                    <i><LuChevronDown /></i>
                  </span>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
      <PrincipleSpotlight
        principle={activePrinciple}
        principleIndex={activePrincipleIndex}
        dialogRef={principleSpotlightRef}
        closeButtonRef={principleSpotlightCloseRef}
        onClose={closePrincipleSpotlight}
      />
    </section>

    <section
      className={`tips-section tips-showcase tips-showcase--practices${practicesVisible ? ' tips-showcase--visible' : ''}${practicesInView ? ' tips-showcase--in-view' : ''}`}
      ref={practicesRef}
      aria-labelledby="practices-title"
    >
      <SectionHeading
        id="practices-title"
        titleFr={<>Bonnes <strong>pratiques</strong></>}
        titleEn={<>Best <strong>practices</strong></>}
      />
      <BilingualLabel
        as="p"
        className="tips-showcase__intro"
        fr="Six gestes simples. Commencez par celui qui vous attire aujourd’hui — quelques minutes suffisent pour retrouver l’élan."
        en="Six simple actions. Start with the one that draws you in today—a few minutes are enough to regain momentum."
      />
      <div className="tips-practice-lab">
        <div className="tips-practice-lab__header" aria-hidden="true">
          <span><i /><BilingualLabel fr="Studio de pratique" en="Practice studio" /></span>
          <BilingualLabel fr="6 gestes · 1 langue active" en="6 actions · 1 active language" />
        </div>
        <div className="tips-practices">
          {practices.map(({
            id,
            icon: Icon,
            labelFr,
            labelEn,
            titleFr,
            titleEn,
            textFr,
            textEn,
            actionFr,
            actionEn,
          }, index) => (
            <article
              className={`tips-practice-card tips-practice-card--${id}`}
              data-tips-practice={id}
              style={{ '--card-index': index }}
              key={id}
            >
              <PracticeMotion type={id} icon={Icon} />
              <div className="tips-practice-card__copy">
                <div className="tips-practice-card__meta">
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <BilingualLabel className="tips-card-meta-label" fr={labelFr} en={labelEn} />
                </div>
                <BilingualLabel as="h3" fr={titleFr} en={titleEn} />
                <BilingualBody className="tips-practice-card__body" fr={textFr} en={textEn} />
                <span className="tips-practice-card__action">
                  <i aria-hidden="true" />
                  <BilingualLabel fr={actionFr} en={actionEn} />
                </span>
              </div>
            </article>
          ))}
        </div>
        <div className="tips-practices__bridge">
          <span className="tips-practices__bridge-mark" aria-hidden="true"><LuSparkles /></span>
          <p>
            <BilingualLabel className="tips-practices__bridge-title" fr="Du conseil à la conversation." en="From guidance to conversation." />
            <BilingualLabel className="tips-practices__bridge-body" fr="Dans OctoSynapse, ces gestes deviennent des réflexes au cœur de scènes immersives." en="In OctoSynapse, these actions become reflexes at the heart of immersive scenes." />
          </p>
          <span className="tips-practices__bridge-status">
            <i />
            <BilingualLabel fr="prêt en 5 min" en="ready in 5 min" />
          </span>
        </div>
      </div>
    </section>

    <section
      className={`tips-section tips-scenes${scenesVisible ? ' tips-scenes--visible' : ''}${scenesInView ? ' tips-scenes--in-view' : ''}`}
      id="resources-scenes"
      ref={scenesRef}
      aria-labelledby="scene-ideas-title"
    >
      <SectionHeading
        id="scene-ideas-title"
        titleFr={<>Idées de scènes <strong>pour pratiquer</strong></>}
        titleEn={<>Scene ideas <strong>for practice</strong></>}
      />
      <div className="tips-scenes__experience">
        <div className="tips-scenes__portal">
          <div className="tips-scenes__portal-mark" aria-hidden="true">
            <AnimatedBrandMark className="tips-scenes__brand-mark" />
            <span className="tips-scenes__orbit tips-scenes__orbit--outer" />
            <span className="tips-scenes__orbit tips-scenes__orbit--inner" />
          </div>
          <div className="tips-scenes__portal-copy">
            <BilingualLabel
              className="tips-scenes__portal-kicker"
              fr="Explorez chaque scène"
              en="Explore each scene"
            />
            <BilingualLabel
              as="p"
              className="tips-scenes__portal-lead"
              fr="Six univers à explorer, chacun avec ses situations et ses réflexes à pratiquer."
              en="Six worlds to explore, each with its own situations and language reflexes to practice."
            />
          </div>
          <div className="tips-scenes__signal" aria-hidden="true">
            <span className="tips-scenes__signal-line" />
            {sceneIdeas.map(({ id, icon: Icon }, index) => (
              <span
                className="tips-scenes__signal-node"
                style={{ '--scene-index': index }}
                key={id}
              >
                <Icon />
              </span>
            ))}
          </div>
        </div>

        <div className="tips-scenes__grid">
          {sceneIdeas.map(({
            id,
            icon: Icon,
            image,
            imageSmall,
            titleFr,
            titleEn,
            textFr,
            textEn,
            categoryFr,
            categoryEn,
            attributes,
            tone,
          }, index) => (
            <article
              className={`tips-scene-card tips-scene-card--${tone}`}
              data-tips-scene={id}
              style={{ '--scene-index': index }}
              key={id}
            >
              <div className="tips-scene-card__visual" aria-hidden="true">
                <img
                  src={image}
                  srcSet={`${imageSmall} 768w, ${image} 1536w`}
                  sizes="(max-width: 620px) calc(100vw - 48px), (max-width: 1060px) calc(50vw - 58px), 540px"
                  alt=""
                  width="1536"
                  height="1024"
                  loading="lazy"
                  decoding="async"
                />
                <span className="tips-scene-card__wash" />
                <span className="tips-scene-card__scanner" />
                <span className="tips-scene-card__number">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <span className="tips-scene-card__scene-icon"><Icon /></span>
              </div>
              <div className="tips-scene-card__copy">
                <BilingualLabel as="h3" fr={titleFr} en={titleEn} />
                <BilingualBody className="tips-scene-card__body" fr={textFr} en={textEn} />
                <dl className="tips-scene-card__attributes">
                  {attributes.map(({ labelFr, labelEn, valueFr, valueEn }) => (
                    <div key={labelFr}>
                      <BilingualLabel as="dt" fr={labelFr} en={labelEn} />
                      <BilingualLabel as="dd" fr={valueFr} en={valueEn} />
                    </div>
                  ))}
                </dl>
              </div>
              <div className="tips-scene-card__footer">
                <span className="tips-scene-card__footer-icon" aria-hidden="true"><Icon /></span>
                <BilingualLabel
                  className="tips-scene-card__category"
                  fr={categoryFr}
                  en={categoryEn}
                />
                <span className="tips-scene-card__live" aria-hidden="true"><i /><i /><i /></span>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>

    <section
      className={`tips-section tips-learning-section${learningVisible ? ' tips-learning-section--visible' : ''}${learningInView ? ' tips-learning-section--in-view' : ''}`}
      ref={learningRef}
      aria-labelledby="learning-title"
    >
      <SectionHeading
        id="learning-title"
        titleFr={<>Acquisition et <strong>réactivation</strong></>}
        titleEn={<>Acquisition and <strong>reactivation</strong></>}
      />
      <BilingualLabel
        as="p"
        className="tips-learning-section__intro"
        fr="Deux chemins différents, un même moteur : votre mémoire. Observez ce qui se construit et ce qui se rallume."
        en="Two different paths, one engine: your memory. See what is built and what is reawakened."
      />
      <div className="tips-learning">
        <div className="tips-learning__topbar">
          <span className="tips-learning__topbar-status">
            <i aria-hidden="true" />
            <BilingualLabel fr="Laboratoire de mémoire" en="Memory lab" />
          </span>
          <BilingualLabel fr="2 chemins · 1 moteur" en="2 paths · 1 engine" />
        </div>

        <div className="tips-learning__flow">
          <svg
            className="tips-learning__continuum"
            viewBox="0 0 1000 270"
            preserveAspectRatio="none"
            aria-hidden="true"
            focusable="false"
          >
            <defs>
              <linearGradient id="tips-learning-route-left" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0" stopColor="#64d8c1" stopOpacity=".18" />
                <stop offset="1" stopColor="#ffd17a" stopOpacity=".86" />
              </linearGradient>
              <linearGradient id="tips-learning-route-right" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0" stopColor="#ffd17a" stopOpacity=".86" />
                <stop offset="1" stopColor="#e693b8" stopOpacity=".18" />
              </linearGradient>
            </defs>
            <path className="tips-learning__continuum-path tips-learning__continuum-path--left" d="M340 135 C405 135 438 135 500 135" pathLength="1" />
            <path className="tips-learning__continuum-path tips-learning__continuum-path--right" d="M500 135 C562 135 595 135 660 135" pathLength="1" />
          </svg>
          <span className="tips-learning__packet tips-learning__packet--left" aria-hidden="true" />
          <span className="tips-learning__packet tips-learning__packet--right" aria-hidden="true" />

          <article
            className="tips-learning__side tips-learning__side--acquisition"
            data-tips-learning="acquisition"
          >
            <LearningMotion
              type="acquisition"
              icon={LuSprout}
              labelFr="Nouveau signal"
              labelEn="New signal"
            />
            <div className="tips-learning__copy">
              <div className="tips-learning__meta">
                <span>01</span>
                <BilingualLabel fr="Construire" en="Build" />
              </div>
              <BilingualLabel as="h3" fr="Acquisition" en="Acquisition" />
              <BilingualLabel as="strong" className="tips-learning__statement" fr="on construit de nouvelles bases" en="Building new foundations" />
              <BilingualBody fr="Vous apprenez du nouveau vocabulaire, des structures et des repères culturels." en="You learn new vocabulary, structures and cultural reference points." />
              <span className="tips-learning__outcome">
                <LuSparkles aria-hidden="true" />
                <BilingualLabel fr="Créer de nouveaux chemins" en="Create new pathways" />
              </span>
            </div>
          </article>

          <div className="tips-learning__engine" aria-hidden="true">
            <span className="tips-learning__engine-halo tips-learning__engine-halo--outer" />
            <span className="tips-learning__engine-halo tips-learning__engine-halo--inner" />
            <TipsMemorySeed className="tips-learning__mark" />
            <div className="tips-learning__engine-copy">
              <BilingualLabel className="tips-learning__engine-title" fr="Une même mémoire" en="One memory system" />
              <span className="tips-learning__engine-status">
                <i />
                <BilingualLabel fr="signal synchronisé" en="signal synchronized" />
              </span>
            </div>
          </div>

          <article
            className="tips-learning__side tips-learning__side--reactivation"
            data-tips-learning="reactivation"
          >
            <LearningMotion
              type="reactivation"
              icon={LuSunrise}
              labelFr="Signal retrouvé"
              labelEn="Signal restored"
            />
            <div className="tips-learning__copy">
              <div className="tips-learning__meta">
                <span>02</span>
                <BilingualLabel fr="Réveiller" en="Reawaken" />
              </div>
              <BilingualLabel as="h3" fr="Réactivation" en="Reactivation" />
              <BilingualLabel as="strong" className="tips-learning__statement" fr="on réveille une langue déjà rencontrée" en="Waking up a language you have encountered before" />
              <BilingualBody fr="Vous récupérez, consolidez et réintégrez ce que vous saviez déjà." en="You retrieve, consolidate and reintegrate what you already knew." />
              <span className="tips-learning__outcome">
                <LuSunrise aria-hidden="true" />
                <BilingualLabel fr="Rallumer les chemins existants" en="Reignite existing pathways" />
              </span>
            </div>
          </article>
        </div>

        <div className="tips-learning__principle">
          <div className="tips-learning__note">
            <LuRefreshCcw aria-hidden="true" />
            <BilingualLabel fr="Dans les deux cas, la mémoire progresse mieux avec répétition, contexte et engagement émotionnel." en="In both cases, memory progresses best through repetition, context and emotional engagement." />
          </div>
          <div className="tips-learning__chips">
            <span>
              <LuRefreshCcw aria-hidden="true" />
              <BilingualLabel fr="Répétition" en="Repetition" />
            </span>
            <span>
              <LuMap aria-hidden="true" />
              <BilingualLabel fr="Contexte" en="Context" />
            </span>
            <span>
              <LuHeart aria-hidden="true" />
              <BilingualLabel fr="Émotion" en="Emotion" />
            </span>
          </div>
        </div>
      </div>
    </section>

    <section className="tips-section tips-faq-section" aria-labelledby="tips-faq-title">
      <SectionHeading
        id="tips-faq-title"
        titleFr={<>Questions <strong>fréquentes</strong></>}
        titleEn={<>Frequently asked <strong>questions</strong></>}
      />
      <div className="tips-faq">
        <div className="tips-faq__grid">
          {faqs.map(({ questionFr, questionEn, answerFr, answerEn }) => (
            <details key={questionFr}>
              <summary>
                <BilingualLabel className="tips-faq__question" fr={questionFr} en={questionEn} />
                <span className="tips-faq__toggle" aria-hidden="true">+</span>
              </summary>
              <BilingualBody className="tips-faq__answer" fr={answerFr} en={answerEn} />
            </details>
          ))}
        </div>
      </div>
    </section>

    <FinalCtaPanel
      headingId="tips-cta-title"
      titleFr="Prêt·e à pratiquer dans des scènes immersives ?"
      titleEn="Ready to practice in immersive scenes?"
      bodyFr="Explorez des situations vivantes, culturelles et mémorables pour remettre votre langue en mouvement."
      bodyEn="Explore vivid, cultural, memorable situations to bring your language back into motion."
      href={APP_ENTRY_HREF}
      label="Explorer les scènes"
      labelEn="Explore scenes"
      activatingLabel="Ouverture des scènes…"
      activatingLabelEn="Opening scenes…"
    />
    </main>
  )
}

export default Resources
